import { useQuery } from '@apollo/client';
import { logError } from 'fergy-core-react-logging';
import { type FunctionComponent, type HTMLAttributes, useContext } from 'react';
import { type MatchingProductsQuery, type MatchingProductsQueryVariables } from '../../../__generated__/graphql-client-types';
import { VariantContext } from '../../../contexts/variant/variant.context';
import { isShowroomOnlyProduct, quotePhrase } from '../../../helpers/product-helper/product.helper';
import { MATCHING_PRODUCTS } from '../../../queries/product/product.queries';
import { type PdpProductFamily } from '../../../types/product.types';
import { doesWindowExist } from '../../../utils/dom/dom.utils';
import { PageLoading } from '../../common-components/loading/loading.component';
import {
	ProductCardList,
	ProductDefaultListCardElement,
	ProductListCardBrandElement
} from '../product-card-list/product-card-list.component';

export type ProductMatchingProductsProps = {
	product: PdpProductFamily;
} & Pick<HTMLAttributes<HTMLElement>, 'className'>;

/**
 * Product Matching Products Panel
 *
 * @param {ProductMatchingProductsProps} props
 */
export const ProductMatchingProducts: FunctionComponent<ProductMatchingProductsProps> = ({ className = 'ph3 pb5', product }) => {
	const { selectedVariant } = useContext(VariantContext);
	const { collection, brandInfo } = product;
	const { data, loading, error } = useQuery<MatchingProductsQuery, MatchingProductsQueryVariables>(MATCHING_PRODUCTS, {
		variables: {
			familyId: product?.id,
			term: collection?.name || '',
			collectionTerm: collection?.name ? `${quotePhrase(brandInfo.name)} ${quotePhrase(collection.name)}` : ''
		},
		skip: !doesWindowExist()
	});

	/* Casting the product family since this component is only used on the PDP
	 * if we want to use it elsewhere the query will need to be modified
	 * collection.name and collection.description will need to be added
	 */

	if (error) {
		logError(error.message);
	}

	const collectionMatches = data?.matchingProducts.collection;
	const accessoriesMatches = data?.matchingProducts.accessories;
	const relatedProductsMatches = data?.matchingProducts.relatedProducts;
	const hasAnyMatchingProducts = Boolean(collectionMatches?.length || accessoriesMatches?.length || relatedProductsMatches?.length);
	const isShowroomProduct = isShowroomOnlyProduct(selectedVariant);
	const shouldDisplay = !isShowroomProduct && brandInfo.name.toLowerCase() !== 'ge';

	if (loading) {
		return <PageLoading />;
	}

	if (error || !hasAnyMatchingProducts) {
		return (
			<div className={className}>
				<div className={`flex flex-column justify-center theme-dark-grey pa3 mt3`}>
					<strong>We don't currently have matching products.</strong>
				</div>
			</div>
		);
	}

	return shouldDisplay ? (
		<div className={className} data-testid="product-matching-products-container">
			{collectionMatches && collectionMatches.length > 0 ? (
				<ProductCardList
					title={`${collection?.name} Collection`}
					description={collection?.description}
					className="mt3"
					headingValue="h3">
					{collectionMatches.map((matchingProduct) => (
						<ProductDefaultListCardElement key={matchingProduct.id} {...matchingProduct} />
					))}
					<ProductListCardBrandElement
						brandName={brandInfo.name}
						collectionName={collection?.name}
						collectionUrl={collection?.url}
					/>
				</ProductCardList>
			) : null}
			{accessoriesMatches && accessoriesMatches.length > 0 ? (
				<ProductCardList title={`Matching Accessories`} className="mt3">
					{accessoriesMatches.map((accessory) => (
						<ProductDefaultListCardElement key={accessory.id} {...accessory} />
					))}
				</ProductCardList>
			) : null}
			{relatedProductsMatches && relatedProductsMatches.length > 0 ? (
				<ProductCardList title={`Related Products`} className="mt3">
					{relatedProductsMatches.map((relatedProduct) => (
						<ProductDefaultListCardElement key={relatedProduct.id} {...relatedProduct} />
					))}
				</ProductCardList>
			) : null}
		</div>
	) : null;
};
